/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Button from "components/generals/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

interface PricingFileUploadProps {
  setIsDataUpdated: (updated: boolean) => void;
  onUploadStart?: () => void;
}

enum UploadMode {
  REPLACE_ALL = "REPLACE_ALL",
  UPDATE_EXISTING = "UPDATE_EXISTING",
  ADD_NEW = "ADD_NEW",
}

interface ProcessResult {
  success: boolean;
  message: string;
  processed: number;
  failed: number;
  errors?: string[];
}

const PricingFileUpload: React.FC<PricingFileUploadProps> = ({
  setIsDataUpdated,
  onUploadStart,
}) => {
  const { t } = useTranslation();
  const [isLoading, error, sendRequest] = useHttp();
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedMode, setSelectedMode] = useState<UploadMode>(UploadMode.ADD_NEW);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadFile = async (file: File, mode: UploadMode): Promise<void> => {
    if (onUploadStart) onUploadStart();

    try {
      setUploadStatus(t("pricingFileUpload.processing"));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", mode);

      await sendRequest<ProcessResult>(
        `${CoreAPI_URLs.PRICING_GENERAL_OPERATIONS}/upload-file`,
        (response: ProcessResult) => {
          if (response.success) {
            setIsDataUpdated(true);
            setUploadStatus(
              `${response.message}\nProcesados: ${response.processed}, Fallidos: ${response.failed}`
            );
          } else {
            setUploadStatus(
              `${response.message}${response.errors ? `\n${response.errors.join("\n")}` : ""}`
            );
          }
        },
        "POST",
        formData,
        null,
        {
          applyErrorFn: (errorMessage: string) => {
            setUploadStatus(`${t("pricingFileUpload.error")}: ${errorMessage}`);
          },
        }
      );

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err: any) {
      let errorMessage = t("pricingFileUpload.unknownError");

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.detail?.message) {
        errorMessage = err.response.data.detail.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setUploadStatus(`${t("pricingFileUpload.error")}: ${errorMessage}`);
    }
  };

  const isValidFileType = (fileName: string): boolean => {
    return fileName.match(/\.(csv|xlsx|xls)$/i) !== null;
  };

  const getFileTypeDescription = (): string => {
    return t("pricingFileUpload.acceptedFormats");
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isValidFileType(file.name)) {
      setUploadStatus(t("pricingFileUpload.invalidFileType"));
      return;
    }

    if (file.size > Number(process.env.REACT_APP_MAX_FILE_SIZE)) {
      setUploadStatus(t("pricingFileUpload.fileTooLarge"));
      return;
    }

    setPendingFile(file);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (pendingFile) {
      setIsModalOpen(false);
      uploadFile(pendingFile, selectedMode);
      setPendingFile(null);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setPendingFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getModeDescription = (mode: UploadMode): string => {
    switch (mode) {
      case UploadMode.REPLACE_ALL:
        return t("pricingFileUpload.replaceAllDescription");
      case UploadMode.UPDATE_EXISTING:
        return t("pricingFileUpload.updateExistingDescription");
      case UploadMode.ADD_NEW:
        return t("pricingFileUpload.addNewDescription");
      default:
        return "";
    }
  };

  const getModeTitle = (mode: UploadMode): string => {
    switch (mode) {
      case UploadMode.REPLACE_ALL:
        return t("pricingFileUpload.replaceAllTitle");
      case UploadMode.UPDATE_EXISTING:
        return t("pricingFileUpload.updateExistingTitle");
      case UploadMode.ADD_NEW:
        return t("pricingFileUpload.addNewTitle");
      default:
        return "";
    }
  };

  const isErrorMessage = (message: string): boolean => {
    return (
      message.includes(t("pricingFileUpload.error")) ||
      message === t("pricingFileUpload.invalidFileType") ||
      message === t("pricingFileUpload.fileTooLarge")
    );
  };

  return (
    <div className="pricing-file-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileSelection}
        style={{ display: "none" }}
      />

      <div className="upload-section">
        <Box display="flex" gap={1} alignItems="center" mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={triggerFileInput}
            disabled={isLoading}
          >
            {isLoading ? t("pricingFileUpload.uploading") : t("pricingFileUpload.uploadFile")}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setShowInstructions(!showInstructions)}
            size="small"
          >
            {showInstructions ? t("common.hide") : t("pricingFileUpload.showInstructions")}
          </Button>
        </Box>

        <Typography variant="h6" className="margin-bottom">
          {getFileTypeDescription()}
        </Typography>

        {showInstructions && (
          <Alert className="transparent-alert" severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" component="div" className="bold-margin">
              {t("pricingFileUpload.fileFormat")}
            </Typography>
            <Box>
              <Typography variant="h6" component="div" className="bold-margin-top">
                {t("pricingFileUpload.supportedFormats")}:
              </Typography>
              <List dense disablePadding className="padded-list">
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.csvTitle")}</strong>:{" "}
                        {t("pricingFileUpload.csvDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.excelTitle")}</strong>:{" "}
                        {t("pricingFileUpload.excelDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
              </List>

              <Typography variant="h6" component="div" className="bold-margin-top">
                {t("pricingFileUpload.requiredColumns")}:
              </Typography>
              <List dense disablePadding className="padded-list">
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.nameTitle")}</strong>:{" "}
                        {t("pricingFileUpload.nameDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.priceTitle")}</strong>:{" "}
                        {t("pricingFileUpload.priceDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
              </List>

              <Typography variant="h6" component="div" className="bold-margin-top">
                {t("pricingFileUpload.optionalColumns")}:
              </Typography>
              <List dense disablePadding className="padded-list">
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.descriptionTitle")}</strong>{" "}
                        {t("pricingFileUpload.descriptionDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.taxTypeTitle")}</strong>:{" "}
                        {t("pricingFileUpload.taxTypeDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.taxAmountTitle")}</strong>:{" "}
                        {t("pricingFileUpload.taxAmountDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary={
                      <>
                        <strong>{t("pricingFileUpload.alternativeTaxTypeTitle")}</strong>:{" "}
                        {t("pricingFileUpload.alternativeTaxTypeDesc")}
                      </>
                    }
                    primaryTypographyProps={{ variant: "h6" }}
                  />
                </ListItem>
              </List>
            </Box>
          </Alert>
        )}
        {isLoading && (
          <Box className="flex-center" mt={2} mb={2}>
            <CircularProgress size={20} className="progress-icon" />
            <Typography variant="h6">{t("pricingFileUpload.processing")}</Typography>
          </Box>
        )}

        {uploadStatus && (
          <Alert severity={isErrorMessage(uploadStatus) ? "error" : "success"} className="status-alert">
            <pre className="pre-wrap">{uploadStatus}</pre>
          </Alert>
        )}

        {error && (
          <Alert severity="error" className="error-alert">
            {t("pricingFileUpload.error")}: {error}
          </Alert>
        )}
      </div>

      <Dialog open={isModalOpen} onClose={handleModalCancel} maxWidth="md" fullWidth>
        <DialogTitle>{t("pricingFileUpload.selectUploadMode")}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" className="margin-bottom">
            {t("pricingFileUpload.selectedFile")}: <strong>{pendingFile?.name}</strong>
          </Typography>

          <FormControl component="fieldset" className="full-width">
            <FormLabel component="legend" className="margin-bottom">
              {t("pricingFileUpload.chooseOption")}
            </FormLabel>
            <RadioGroup
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value as UploadMode)}
            >
              <FormControlLabel
                value={UploadMode.REPLACE_ALL}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle1" className="bold-font">
                      {getModeTitle(UploadMode.REPLACE_ALL)}
                    </Typography>
                    <Typography variant="h6" className="thin-font">
                      {getModeDescription(UploadMode.REPLACE_ALL)}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: "flex-start" }}
              />
              <FormControlLabel
                value={UploadMode.UPDATE_EXISTING}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle1" className="bold-font">
                      {getModeTitle(UploadMode.UPDATE_EXISTING)}
                    </Typography>
                    <Typography variant="h6" className="thin-font">
                      {getModeDescription(UploadMode.UPDATE_EXISTING)}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: "flex-start" }}
              />
              <FormControlLabel
                value={UploadMode.ADD_NEW}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle1" className="bold-font">
                      {getModeTitle(UploadMode.ADD_NEW)}
                    </Typography>
                    <Typography variant="h6" className="thin-font">
                      {getModeDescription(UploadMode.ADD_NEW)}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start" }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalCancel} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleModalConfirm} color="primary" variant="contained">
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PricingFileUpload;
